<template>
  <Teleport
    to="#pinbox-dock"
    defer
    :disabled="!store.docked">
    <div
      v-if="store.hasPinned && !store.hidden"
      ref="panelRef"
      :class="[
        'pinned-shares-panel',
        {
          'pinned-shares-panel--light': !$q.dark.isActive,
          'pinned-shares-panel--docked': store.docked,
        },
      ]"
      :style="panelStyle"
      @pointerdown="onPanelPointerDown"
      @pointermove="onPanelPointerMove"
      @pointerup="onPanelPointerUp"
      @pointercancel="onPanelPointerUp">
    <div
      class="panel-header"
      @pointerdown="onDragStart">
      <q-icon
        :name="symRoundedKeep"
        class="q-mr-sm header-icon" />
      <span class="text-subtitle2 text-weight-medium">
        {{ t('crudmanager.share.pinned.title') }}
      </span>
      <q-badge
        outline
        color="primary"
        :label="store.count"
        class="q-ml-sm header-count" />
      <q-space />
      <q-btn
        v-if="!isDefaultSize"
        flat
        round
        dense
        color="grey-5"
        class="reset-size-btn"
        :icon="symRoundedAspectRatio"
        @click.stop="resetSize">
        <q-tooltip>{{ t('crudmanager.share.pinned.resetSize') }}</q-tooltip>
      </q-btn>
      <q-space />
      <q-btn
        flat
        round
        dense
        :icon="symRoundedCloseFullscreen"
        @click.stop="store.hide" />
    </div>

    <div class="panel-body">
      <q-slide-item
        v-for="share in store.pinned"
        :key="share.id"
        left-color="negative"
        class="pinned-share-slide"
        @left="onSlideLeft(share, $event)">
        <template #left>
          <div class="slide-action slide-action-left">
            <q-icon
              :name="symRoundedKeepOff"
              size="20px"
              class="q-mr-sm" />
            {{ t('crudmanager.share.pinned.done') }}
          </div>
        </template>
        <div
          class="pinned-share"
          :class="{ 'pinned-share-collapsed': isShareCollapsed(share.id) }">
          <div class="row items-center no-wrap q-mb-xs">
          <q-checkbox
            :model-value="isShareSelected(share)"
            size="sm"
            dense
            class="share-check q-mr-sm"
            @update:model-value="(v) => toggleShare(share, v)"
            @click.stop />
          <div class="col min-w-0">
            <div
              v-if="share.note"
              class="text-body2 note">
              {{ share.note }}
            </div>
            <div
              v-else-if="firstSourceItem(share)"
              class="text-body2 note note-fallback">
              <CrudPreview
                :model-name="firstSourceItem(share).modelName"
                :model-id="firstSourceItem(share).modelId"
                size="sm"
                text-only />
            </div>
            <div class="meta-line">
              <span class="meta-sender">
                <q-icon
                  :name="isSelfPin(share) ? symRoundedKeep : symRoundedShare"
                  class="q-mr-xs" />
                <template v-if="isSelfPin(share)">{{ t('crudmanager.share.pinned.byYou') }}</template>
                <template v-else>{{ share.senderName ?? '?' }}</template>
              </span>
              <span
                v-if="share.sharedAt || share.pinnedAt"
                class="meta-sep">·</span>
              <span
                v-if="share.sharedAt || share.pinnedAt"
                class="meta-date">
                {{ formatShareDate(share) }}
                <q-tooltip v-if="share.sharedAt">
                  {{ new Date(share.sharedAt).toLocaleString() }}
                </q-tooltip>
              </span>
            </div>
          </div>
          <q-btn
            flat
            round
            dense
            class="card-action"
            :icon="isShareCollapsed(share.id) ? symRoundedKeyboardArrowDown : symRoundedKeyboardArrowUp"
            @click="toggleShareCollapsed(share.id)" />
        </div>

        <q-list
          v-if="!isShareCollapsed(share.id)"
          dense
          class="items-list">
          <q-item
            v-for="item in share.items"
            :key="`${item.modelName}-${item.modelId}`"
            :clickable="!isActiveItem(item)"
            :class="[
              'q-px-xs',
              `depth-${cappedDepth(item)}`,
              {
                'pinned-share-root': cappedDepth(item) === 0,
                'pinned-share-active': isActiveItem(item),
              },
            ]"
            @click="!isActiveItem(item) && openItem(item.modelName, item.modelId)">
            <q-item-section class="preview-cell">
              <CrudPreview
                :model-name="item.modelName"
                :model-id="item.modelId"
                size="sm"
                text-only />
            </q-item-section>
            <q-item-section
              side
              class="info-cell"
              @click.stop>
              <CrudPreview
                :model-name="item.modelName"
                :model-id="item.modelId"
                color="primary"
                btn-only />
            </q-item-section>
          </q-item>
        </q-list>

        </div>
      </q-slide-item>
    </div>

    <div
      v-if="selectedCount > 0"
      class="panel-share">
      <div class="share-heading">
        <q-btn
          flat
          dense
          round
          :icon="symRoundedClose"
          @click="clearSelection">
          <q-tooltip>{{ t('crudmanager.share.pin.selectNone') }}</q-tooltip>
        </q-btn>
        <span class="share-heading-label">
          {{ t('crudmanager.share.pinned.selectedCount', selectedCount, { count: selectedCount }) }}
        </span>
        <q-space />
        <q-btn
          flat
          dense
          round
          color="negative"
          :icon="symRoundedKeepOff"
          @click="submitUnpin">
          <q-tooltip>
            {{ t('crudmanager.share.pinned.unpinSelected', selectedCount, { count: selectedCount }) }}
          </q-tooltip>
        </q-btn>
        <q-btn
          flat
          dense
          round
          color="primary"
          :icon="symRoundedShare"
          @click="shareExpanded = !shareExpanded">
          <q-tooltip>{{ t('crudmanager.share.action') }}</q-tooltip>
        </q-btn>
      </div>

      <q-slide-transition>
        <div v-if="shareExpanded">
          <q-input
            v-model="shareNote"
            class="share-field share-note"
            :label="t('crudmanager.share.note')"
            type="textarea"
            autogrow
            dense
            maxlength="1000"
            outlined />
          <Suspense>
            <CrudSelect
              v-model="recipientIds"
              outlined
              dense
              model-name="user"
              :label="t('crudmanager.share.pin.recipientsLabel')"
              value-field="id"
              label-field="full_name"
              :filters="recipientFilters"
              multiple
              class="share-field" />
            <template #fallback>
              <q-skeleton
                type="QInput"
                height="32px" />
            </template>
          </Suspense>
          <div class="share-actions">
            <q-btn
              unelevated
              dense
              size="sm"
              color="primary"
              :icon="symRoundedShare"
              :label="t('crudmanager.share.send')"
              :loading="sharing"
              :disable="recipientIds.length === 0"
              class="share-send-btn"
              @click="submitShare" />
          </div>
        </div>
      </q-slide-transition>
    </div>

    <div
      v-if="!store.docked"
      class="resize-handle"
      @pointerdown="onResizeStart">
      <div class="resize-grip" />
    </div>
  </div>
  </Teleport>

  <Teleport to="body">
    <div
      v-if="dragging && dropZoneRect"
      class="pinbox-drop-preview"
      :class="{ 'pinbox-drop-preview--active': dropZoneActive }"
      :style="dropZoneStyle" />
  </Teleport>
</template>

<script setup lang="ts">
import { symRoundedAspectRatio, symRoundedClose, symRoundedCloseFullscreen, symRoundedKeep, symRoundedKeepOff, symRoundedKeyboardArrowDown, symRoundedKeyboardArrowUp, symRoundedShare } from '@quasar/extras/material-symbols-rounded'
import { useQuasar } from 'quasar'
import CrudPreview from 'src/CrudManager/Core/composites/Editors/CrudPreview/CrudPreview.vue'
import CrudSelect from 'src/CrudManager/Core/composites/Form/CrudSelect.vue'
import { useCrudManagerStore } from 'src/CrudManager/Core/stores/useCrudManagerStore'
import { usePinnedSharesStore, type PinnedShare, type PinnedShareItem } from 'src/CrudManager/Core/stores/usePinnedSharesStore'
import { useUserStore } from 'src/CrudManager/Core/stores/useUserStore'
import { axiosApi } from 'src/CrudManager/Core/Utils/axios'
import { showErrorNotification, showSuccessNotification } from 'src/CrudManager/Core/Utils/ToastNotification'
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import useCrudUtils from '../../composables/CrudUtils/useCrudUtils'
import useDate from '../../composables/useDate'

const store = usePinnedSharesStore()
const userStore = useUserStore()
const crudManagerStore = useCrudManagerStore()
const { t } = useI18n()
// Bound in the template to flip the panel surface to a light variant when the
// page theme is light. Inner element styles stay untouched so dark mode keeps
// the prior look.
const $q = useQuasar()
const { openEditor, generateModuleUrl } = useCrudUtils()
const route = useRoute()

function isActiveItem(item: PinnedShareItem): boolean {
  const url = generateModuleUrl(item.modelName, 'editor', String(item.modelId))
  return !!url && url === route.path
}
const { timeAgo } = useDate()

const myId = computed(() => (userStore.user?.id ? String(userStore.user.id) : null))

const recipientFilters = computed(() => {
  const filters: { field: string; operator: string; value: string }[] = []
  if (myId.value) {
    filters.push({ field: 'id', operator: '!=', value: myId.value })
  }
  return filters
})

const selection = reactive(new Set<string>())

function isShareCollapsed(id: string): boolean {
  return store.isShareCollapsed(id)
}

function toggleShareCollapsed(id: string) {
  store.toggleShareCollapsed(id)
}
const recipientIds = ref<(string | number)[]>([])
const shareNote = ref('')
const sharing = ref(false)
// The share form is collapsed by default — selecting items shows a compact
// action row (unpin / share toggle / clear). Clicking the share icon expands
// the recipient + note inputs below.
const shareExpanded = ref(false)

function isShareSelected(share: PinnedShare): boolean {
  return selection.has(share.id)
}

function toggleShare(share: PinnedShare, on: boolean) {
  if (on) selection.add(share.id)
  else selection.delete(share.id)
}

const selectedCount = computed(() => selection.size)

function clearSelection() {
  selection.clear()
  recipientIds.value = []
  shareNote.value = ''
  shareExpanded.value = false
}

function selectedItemsPayload(): { model_name: string; model_id: string; kind: 'source' | 'linked'; depth: number; parent_model_name: string | null; parent_model_id: string | null }[] {
  const out: ReturnType<typeof selectedItemsPayload> = []
  const seen = new Set<string>()
  for (const share of store.pinned) {
    if (!selection.has(share.id)) continue
    for (const item of share.items) {
      const key = `${item.modelName}|${item.modelId}`
      if (seen.has(key)) continue
      seen.add(key)
      out.push({
        model_name: item.modelName,
        model_id: item.modelId,
        kind: (item.kind ?? 'source') as 'source' | 'linked',
        depth: item.depth ?? 0,
        parent_model_name: item.parentModelName ?? null,
        parent_model_id: item.parentModelId ?? null,
      })
    }
  }
  return out
}

async function submitShare() {
  const items = selectedItemsPayload()
  if (items.length === 0 || recipientIds.value.length === 0) return
  sharing.value = true
  try {
    const { data } = await axiosApi.post('cm/share', {
      recipient_ids: recipientIds.value,
      note: shareNote.value || null,
      items,
    })
    if (data?.result === 'success') {
      showSuccessNotification(t('crudmanager.share.success'))
      clearSelection()
    } else {
      showErrorNotification(data?.message ?? t('crudmanager.share.error'))
    }
  } catch (e) {
    const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message
    showErrorNotification(msg ?? t('crudmanager.share.error'))
  } finally {
    sharing.value = false
  }
}

function submitUnpin() {
  if (selection.size === 0) return
  // Snapshot the ids first — `store.unpin` mutates the array we'd otherwise
  // be iterating over indirectly via reactive bindings.
  const ids = [...selection]
  for (const id of ids) store.unpin(id)
  clearSelection()
}

function onWindowResize() {
  // Re-clamp persisted position back into the viewport when the window shrinks
  // (typical when unplugging an external monitor). Height is CSS-driven so
  // nothing else needs to react here.
  store.clampToViewport(window.innerWidth, window.innerHeight)
}

let bandObserver: ResizeObserver | null = null

// Below this band height the docked panel renders as an unusable sliver;
// pop back to floating instead.
const MIN_DOCK_BAND = 180

function recomputeDockBand() {
  const aside = document.querySelector<HTMLElement>('aside.q-drawer--left')
  if (!aside || aside.offsetWidth < 100) {
    dockBand.value = null
    if (store.docked) store.setDocked(false)
    return
  }
  const drawer = aside.getBoundingClientRect()
  const list = aside.querySelector<HTMLElement>('.sidebar-list')
  const recents = aside.querySelector<HTMLElement>('.sidebar-recents')
  if (!list || !recents) {
    dockBand.value = null
    return
  }
  const listR = list.getBoundingClientRect()
  const recentsR = recents.getBoundingClientRect()
  const bandTop = listR.bottom - drawer.top
  const bandBottom = recentsR.top - drawer.top
  const bandHeight = bandBottom - bandTop
  if (bandHeight < MIN_DOCK_BAND) {
    dockBand.value = null
    if (store.docked) store.setDocked(false)
    return
  }
  // Cap height so the panel never overlaps a slow recents-list reflow, leave
  // a small breathing margin so the dashed drop preview doesn't hug recents.
  const margin = 8
  const usable = bandHeight - margin * 2
  const top = bandTop + margin
  dockBand.value = { top, height: usable }
}

function installBandObserver() {
  bandObserver?.disconnect()
  const aside = document.querySelector<HTMLElement>('aside.q-drawer--left')
  const list = aside?.querySelector<HTMLElement>('.sidebar-list')
  const recents = aside?.querySelector<HTMLElement>('.sidebar-recents')
  if (!aside) return
  bandObserver = new ResizeObserver(() => recomputeDockBand())
  bandObserver.observe(aside)
  if (list) bandObserver.observe(list)
  if (recents) bandObserver.observe(recents)
}

onMounted(() => {
  store.migrateLegacy()
  store.clampToViewport(window.innerWidth, window.innerHeight)
  window.addEventListener('resize', onWindowResize)
  // Defer one tick so q-drawer's aside element is mounted before we observe it.
  void nextTick(() => {
    installBandObserver()
    recomputeDockBand()
  })
})

watch(() => store.docked, (docked) => {
  if (docked) {
    void nextTick(() => recomputeDockBand())
  }
})

// Drawer hidden (mobile close, programmatic toggle) → undock so the panel
// doesn't render into a detached Teleport target. Immediate because the layout
// settles the drawer for a phone viewport during its own setup, before this
// panel exists to hear it: a docked panel would otherwise boot detached.
watch(
  () => crudManagerStore.leftDrawerOpen,
  (open) => {
    if (!open && store.docked) {
      store.setDocked(false)
    }
  },
  { immediate: true },
)

function formatShareDate(share: PinnedShare): string {
  const ts = share.sharedAt ?? new Date(share.pinnedAt).toISOString()
  return timeAgo(ts)
}

function firstSourceItem(share: PinnedShare): PinnedShareItem | null {
  return share.items.find((i) => (i.kind ?? 'source') === 'source') ?? share.items[0] ?? null
}

function isSelfPin(share: PinnedShare): boolean {
  const me = userStore.user?.id
  return !!me && !!share.senderId && String(share.senderId) === String(me)
}

const MAX_INDENT_DEPTH = 4

function cappedDepth(item: { depth?: number }): number {
  // Cap so a deeply-nested ref chain doesn't push the row off the right edge
  // of the 295px-min panel and clip the preview into nothing.
  return Math.min(MAX_INDENT_DEPTH, Math.max(0, item.depth ?? 0))
}

const panelRef = ref<HTMLDivElement | null>(null)
const dragging = ref(false)
const dragOffset = ref({ x: 0, y: 0 })

const MIN_W = 295
const DEFAULT_W = 340

const panelWidth = computed(() => {
  // Prefer the in-flight pinch size so pinch updates feel live without writing
  // to localStorage on every pointermove (the commit happens on pointerup).
  return pinchPendingWidth.value ?? store.panelSize?.w ?? DEFAULT_W
})

const isDefaultSize = computed(() => store.panelSize === null || store.panelSize.w === DEFAULT_W)

function resetSize() {
  store.resetSize()
}

const dockBand = ref<{ top: number; height: number } | null>(null)

const panelStyle = computed(() => {
  if (store.docked) {
    if (!dockBand.value) return { visibility: 'hidden' }
    return {
      top: `${dockBand.value.top}px`,
      height: `${dockBand.value.height}px`,
    }
  }
  const w = panelWidth.value
  const pos = store.panelPosition ?? { x: window.innerWidth - w, y: 80 }
  return {
    left: `${pos.x}px`,
    top: `${pos.y}px`,
    width: `${w}px`,
  }
})

const resizing = ref(false)
const resizeStart = ref({ x: 0, w: 0 })
const resizePointerId = ref<number | null>(null)

function onResizeStart(event: PointerEvent) {
  event.preventDefault()
  event.stopPropagation()
  const target = event.currentTarget as HTMLElement | null
  if (!target) return
  resizing.value = true
  resizeStart.value = {
    x: event.clientX,
    w: panelWidth.value,
  }
  resizePointerId.value = event.pointerId
  target.setPointerCapture(event.pointerId)
  target.addEventListener('pointermove', onResizeMove)
  target.addEventListener('pointerup', onResizeEnd)
  target.addEventListener('pointercancel', onResizeEnd)
}

function onResizeMove(event: PointerEvent) {
  if (!resizing.value) return
  const maxW = Math.max(MIN_W, window.innerWidth * 0.95)
  const w = Math.max(MIN_W, Math.min(maxW, resizeStart.value.w + (event.clientX - resizeStart.value.x)))
  store.setSize(w, 0)
}

function onResizeEnd(event: PointerEvent) {
  resizing.value = false
  const target = event.currentTarget as HTMLElement | null
  if (target) {
    target.removeEventListener('pointermove', onResizeMove)
    target.removeEventListener('pointerup', onResizeEnd)
    target.removeEventListener('pointercancel', onResizeEnd)
    if (resizePointerId.value !== null && target.hasPointerCapture(resizePointerId.value)) {
      target.releasePointerCapture(resizePointerId.value)
    }
  }
  resizePointerId.value = null
}

const pinchPointers = new Map<number, { x: number; y: number }>()
const pinchStart = ref<{ dist: number; w: number } | null>(null)
// Live pinch width: drives the panel's reactive width during the gesture
// without persisting on every pointermove. Committed to the store on the
// final pointerup so localStorage takes one write per gesture.
const pinchPendingWidth = ref<number | null>(null)

function pinchDistance(): number {
  const pts = [...pinchPointers.values()]
  if (pts.length < 2) return 0
  const [a, b] = pts
  return Math.hypot(b.x - a.x, b.y - a.y)
}

function onPanelPointerDown(event: PointerEvent) {
  if (event.pointerType !== 'touch') return
  pinchPointers.set(event.pointerId, { x: event.clientX, y: event.clientY })
  if (pinchPointers.size === 2) {
    // Cancel any header drag in progress so pinch takes over cleanly.
    if (dragging.value) {
      dragging.value = false
      window.removeEventListener('pointermove', onDragMove)
      window.removeEventListener('pointerup', onDragEnd)
      window.removeEventListener('pointercancel', onDragEnd)
    }
    pinchStart.value = {
      dist: pinchDistance(),
      w: panelWidth.value,
    }
    event.preventDefault()
  }
}

function onPanelPointerMove(event: PointerEvent) {
  if (event.pointerType !== 'touch') return
  if (!pinchPointers.has(event.pointerId)) return
  pinchPointers.set(event.pointerId, { x: event.clientX, y: event.clientY })
  if (!pinchStart.value || pinchPointers.size < 2) return
  event.preventDefault()
  const dist = pinchDistance()
  if (dist === 0 || pinchStart.value.dist === 0) return
  const ratio = dist / pinchStart.value.dist
  const maxW = Math.max(MIN_W, window.innerWidth * 0.95)
  pinchPendingWidth.value = Math.max(MIN_W, Math.min(maxW, pinchStart.value.w * ratio))
}

function onPanelPointerUp(event: PointerEvent) {
  if (event.pointerType !== 'touch') return
  pinchPointers.delete(event.pointerId)
  if (pinchPointers.size < 2) {
    pinchStart.value = null
    if (pinchPendingWidth.value !== null) {
      store.setSize(pinchPendingWidth.value, 0)
      pinchPendingWidth.value = null
    }
  }
}

// Drop-zone state: a band inside the left drawer between the scroll area and
// the recents list. Refreshed at drag-start so layout shifts (drawer mini,
// theme switch) don't leave stale rects behind.
interface DropZoneRect { top: number; left: number; width: number; height: number }
const dropZoneRect = ref<DropZoneRect | null>(null)
const dropZoneActive = ref(false)
const drawerEl = ref<HTMLElement | null>(null)
const sidebarListEl = ref<HTMLElement | null>(null)
const sidebarRecentsEl = ref<HTMLElement | null>(null)

function refreshDropZoneRefs() {
  drawerEl.value = document.querySelector<HTMLElement>('aside.q-drawer--left')
  sidebarListEl.value = drawerEl.value?.querySelector<HTMLElement>('.sidebar-list') ?? null
  sidebarRecentsEl.value = drawerEl.value?.querySelector<HTMLElement>('.sidebar-recents') ?? null
}

function recomputeDropZoneRect(): DropZoneRect | null {
  if (!drawerEl.value) return null
  const drawer = drawerEl.value.getBoundingClientRect()
  if (drawer.width < 100) return null
  const recentsR = sidebarRecentsEl.value?.getBoundingClientRect()
  const listR = sidebarListEl.value?.getBoundingClientRect()
  // Bottom edge: top of recents (or 60px above drawer bottom as fallback) so
  // the dock target never overlaps recents/profile.
  const bottom = recentsR ? recentsR.top : drawer.bottom - 60
  // Top edge: bottom of the rendered items list so the full empty band
  // between items and recents catches a drop. Falls back to a fixed 160px
  // band when the list ref isn't found yet.
  const top = listR ? listR.bottom : bottom - 160
  if (bottom <= top + 8) return null
  return {
    top,
    left: drawer.left,
    width: drawer.width,
    height: bottom - top,
  }
}

const dropZoneStyle = computed(() => {
  const r = dropZoneRect.value
  if (!r) return {}
  return {
    top: `${r.top}px`,
    left: `${r.left}px`,
    width: `${r.width}px`,
    height: `${r.height}px`,
  }
})

function updateDropZone(clientX: number, clientY: number) {
  const r = recomputeDropZoneRect()
  dropZoneRect.value = r
  if (!r) {
    dropZoneActive.value = false
    return
  }
  dropZoneActive.value =
    clientX >= r.left
    && clientX <= r.left + r.width
    && clientY >= r.top
    && clientY <= r.top + r.height
}

function onDragStart(event: PointerEvent) {
  const origin = event.target as HTMLElement | null
  // Skip drag init when the gesture starts on a header button so close /
  // collapse / reset-size buttons still receive their click.
  if (origin?.closest('.q-btn')) return
  if (!panelRef.value) return
  const rect = panelRef.value.getBoundingClientRect()
  dragOffset.value = { x: event.clientX - rect.left, y: event.clientY - rect.top }
  // Undock immediately so the panel detaches from the flex slot and follows
  // the cursor. Seed floating coords from the current rect so it doesn't jump.
  if (store.docked) {
    if (!store.panelSize) store.setSize(Math.max(MIN_W, rect.width), 0)
    store.setPosition(rect.left, rect.top)
    store.setDocked(false)
  }
  dragging.value = true
  refreshDropZoneRefs()
  updateDropZone(event.clientX, event.clientY)
  // Window-bound listeners survive Teleport DOM reparenting (the dock toggle
  // releases pointer capture). Pointer events from a button-down gesture keep
  // dispatching globally until pointerup, so capture is unnecessary.
  window.addEventListener('pointermove', onDragMove)
  window.addEventListener('pointerup', onDragEnd)
  window.addEventListener('pointercancel', onDragEnd)
  event.preventDefault()
}

function onDragMove(event: PointerEvent) {
  if (!dragging.value || !panelRef.value) return
  const panelW = panelRef.value.offsetWidth
  const panelH = panelRef.value.offsetHeight
  const maxX = Math.max(0, window.innerWidth - panelW)
  const maxY = Math.max(0, window.innerHeight - Math.min(panelH, 60))
  const x = Math.max(0, Math.min(maxX, event.clientX - dragOffset.value.x))
  const y = Math.max(0, Math.min(maxY, event.clientY - dragOffset.value.y))
  store.setPosition(x, y)
  updateDropZone(event.clientX, event.clientY)
}

function onDragEnd() {
  const wasDragging = dragging.value
  const dropped = wasDragging && dropZoneActive.value
  dragging.value = false
  dropZoneRect.value = null
  dropZoneActive.value = false
  window.removeEventListener('pointermove', onDragMove)
  window.removeEventListener('pointerup', onDragEnd)
  window.removeEventListener('pointercancel', onDragEnd)
  if (dropped) {
    store.setDocked(true)
  }
}

onBeforeUnmount(() => {
  // Mid-flight gesture: drop the listeners we attached on window so they
  // don't keep firing after teardown.
  dragging.value = false
  window.removeEventListener('pointermove', onDragMove)
  window.removeEventListener('pointerup', onDragEnd)
  window.removeEventListener('pointercancel', onDragEnd)
  window.removeEventListener('resize', onWindowResize)
  bandObserver?.disconnect()
})

function openItem(modelName: string, modelId: string) {
  openEditor(modelName, modelId)
}

function markDone(share: PinnedShare) {
  selection.delete(share.id)
  store.unpin(share.id)
}

function onSlideLeft(share: PinnedShare, { reset }: { reset: () => void }) {
  // Snap the slide back to center first while the row's still mounted, then
  // remove it. Doing it the other way around leaves `reset()` operating on a
  // DOM node Vue is in the process of unmounting, which is a no-op visually.
  reset()
  markDone(share)
}
</script>

<style scoped lang="scss">
.pinned-shares-panel {
  position: fixed;
  z-index: 4000;
  min-width: 295px;
  height: auto;
  max-width: 95vw;
  max-height: 90vh;
  background: #14181e;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.55), 0 1px 0 rgba(255, 255, 255, 0.04) inset;
  color: #e7eaee;
  user-select: none;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  // Pinch-to-resize: block default browser pinch-zoom inside the panel so JS sees both pointers.
  touch-action: none;

  // QCheckbox auto-picks colors from the surrounding light/dark theme via
  // body classes, but our panel is always dark regardless of the page theme.
  // Mirror Quasar's `q-checkbox--dark` modifier locally so unchecked boxes
  // render with light borders instead of near-invisible dark on dark.
  :deep(.q-checkbox__inner) {
    color: rgba(255, 255, 255, 0.7);
  }

}

// Light-page variant: panel surface flips to a soft white and the strictly
// hard-coded "white text" elements (note body, meta line, header label) flip
// with it. Hover tints, item-list rails, and the active blue gradient are
// already low-alpha enough to read on the lighter surface, so they stay put —
// dark mode looks identical to before this commit.
.pinned-shares-panel--light {
  background: #f7f8fa;
  border-color: rgba(0, 0, 0, 0.08);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18), 0 1px 0 rgba(255, 255, 255, 0.6) inset;
  color: #1a1f29;

  .panel-header {
    background: linear-gradient(180deg, rgba(0, 0, 0, 0.025), rgba(0, 0, 0, 0.005));
    color: #1a1f29;
    border-bottom-color: rgba(0, 0, 0, 0.06);
  }

  .panel-share {
    border-top-color: rgba(0, 0, 0, 0.06);
    background: rgba(0, 0, 0, 0.02);
  }

  .share-heading {
    color: #4b5563;
  }

  .pinned-share {
    background: rgba(0, 0, 0, 0.025);
    border-color: rgba(0, 0, 0, 0.06);

    &:hover {
      background: rgba(0, 0, 0, 0.045);
      border-color: rgba(0, 0, 0, 0.09);
    }
  }

  .note { color: #1a1f29; }
  .meta-line { color: #6b7280; }
  .meta-sender { color: #4b5563; }

  .items-list {
    background: rgba(0, 0, 0, 0.04);
  }

  .items-list :deep(.q-item) {
    &:hover,
    &:active {
      background: rgba(96, 165, 250, 0.18);

      .preview-cell {
        text-decoration-color: rgba(29, 78, 216, 0.55);
      }
    }

    &::before {
      background: rgba(0, 0, 0, 0.12);
    }

    .preview-cell {
      color: #1d4ed8;
    }
  }

  .items-list :deep(.q-item.pinned-share-root) {
    &:not(:first-child) {
      border-top-color: rgba(0, 0, 0, 0.1);
    }

    .preview-cell {
      color: #1e3a8a;
    }
  }

  .pinned-share-active :deep(.preview-cell) {
    color: #ffffff !important;
  }

  .resize-grip {
    background: rgba(0, 0, 0, 0.3);
  }

  .resize-handle:hover .resize-grip {
    background: rgba(0, 0, 0, 0.55);
  }

  // Quasar's checkbox auto-picks light-theme colors via the body class — the
  // dark-mirror override above no longer applies here.
  :deep(.q-checkbox__inner) {
    color: rgba(0, 0, 0, 0.55);
  }

  // Match the slide-item content surface to the light panel chrome so a
  // half-swiped-then-cancelled gesture keeps the row opaque over the red
  // "Terminé" action.
  .pinned-share-slide {
    background: #f7f8fa;
  }

  // Quasar's grey-5 / grey-6 tokens are tuned for a dark surface; on the
  // light panel they read as low-contrast against the white-ish background.
  // Push them darker so chrome icons remain legible.
  :deep(.text-grey-5) { color: #6b7280 !important; }
  :deep(.text-grey-6) { color: #4b5563 !important; }

  // Quasar's `negative` token (#C10015) is too saturated against a light
  // surface — the unpin button "burns" the eye next to neutral text. Use a
  // softer red that still reads as destructive.
  :deep(.text-negative) { color: #dc2626 !important; }
  :deep(.bg-negative) { background: #dc2626 !important; }
}

// Docked variant: panel becomes a flex row inside the sidebar slot. Width
// follows the drawer; max-height keeps the body scrollable so a long pinned
// list can't push the recents/profile rows off-screen.
.pinned-shares-panel--docked {
  position: absolute;
  left: 0;
  right: 0;
  width: auto !important;
  min-width: 0;
  max-width: none;
  min-height: 0;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.35);

  .panel-header {
    border-radius: 6px 6px 0 0;
  }
}

.pinbox-drop-preview {
  position: fixed;
  z-index: 4001;
  pointer-events: none;
  background: rgba(96, 165, 250, 0.10);
  border: 2px dashed rgba(96, 165, 250, 0.55);
  border-radius: 6px;
  transition: background 0.1s, border-color 0.1s;
}

.pinbox-drop-preview--active {
  background: rgba(96, 165, 250, 0.22);
  border-color: rgba(96, 165, 250, 0.95);
}

.resize-handle {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 8px;
  cursor: ew-resize;
  touch-action: none;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
}

.reset-size-btn {
  border-radius: 999px;
}

.resize-grip {
  width: 2px;
  height: 28px;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.25);
  transition: background 0.15s;

  .resize-handle:hover & {
    background: rgba(255, 255, 255, 0.5);
  }
}

@media (max-width: 599px) {
  .resize-handle {
    width: 14px;
  }

  .resize-grip {
    width: 3px;
    height: 36px;
  }
}

.panel-header {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  cursor: grab;
  flex: 0 0 auto;
  position: relative;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.04), rgba(255, 255, 255, 0.01));
  color: #f1f4f8;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px 12px 0 0;
  // Without this, mobile browsers interpret pointermove as a scroll/pan gesture
  // and emit only a few events before cancelling — drag barely moves.
  touch-action: none;

  &:active {
    cursor: grabbing;
  }
}

.header-icon {
  color: #4dd0e1;
}

.header-count {
  font-size: 0.7rem;
  padding: 0 6px;
  height: 18px;
}

.min-w-0 {
  min-width: 0;
}

.pinned-share-collapsed {
  padding-bottom: 8px;
}

.panel-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 10px;
  // Re-enable single-finger vertical scroll inside the body. Two-finger gestures
  // still bubble up as pointer events for pinch-to-resize.
  touch-action: pan-y;
}

.panel-share {
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  padding: 8px 10px 10px;
  background: rgba(255, 255, 255, 0.02);
  flex: 0 0 auto;
  max-height: 50%;
  overflow-y: auto;
  touch-action: pan-y;
}

.share-heading {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  font-size: 0.7rem;
  color: #b8c0cc;
}

.share-heading-label {
  font-weight: 500;
  letter-spacing: 0.01em;
}

.share-field {
  margin-bottom: 6px;

  :deep(.q-field__control),
  :deep(.q-field__marginal) {
    min-height: 32px;
    height: 32px;
  }

  :deep(.q-field__native),
  :deep(.q-field__input) {
    font-size: 0.78rem;
    padding: 0;
    min-height: 32px;
  }

  :deep(.q-field__label) {
    font-size: 0.72rem;
    top: 8px;
  }

  :deep(.q-field--float .q-field__label) {
    transform: translateY(-32%) scale(0.78);
  }

  :deep(.q-chip) {
    font-size: 0.7rem;
    height: 22px;
  }
}

.share-note {
  :deep(.q-field__control),
  :deep(.q-field__marginal) {
    min-height: 36px;
    height: auto;
  }

  :deep(textarea.q-field__native) {
    min-height: 28px;
    line-height: 1.25;
  }
}

.share-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 4px;
}

.share-send-btn {
  letter-spacing: 0.02em;
}

.items-list :deep(.q-item) {
  position: relative;
  min-width: 0;
  overflow: visible;
  cursor: pointer;
  border-radius: 4px;
  transition: background 0.15s;
  min-height: 28px;
  padding-top: 2px;
  padding-bottom: 2px;

  &:hover,
  &:active {
    background: rgba(96, 165, 250, 0.12);

    .preview-cell {
      text-decoration: underline;
      text-decoration-color: rgba(147, 197, 253, 0.55);
      text-underline-offset: 2px;
    }
  }

  // Color-only link cue (underline only on hover) so a stack of rows reads
  // as a list, not a wall of underlines.
  .preview-cell {
    color: #93c5fd;
  }
}

// Tree guide rails for linked items. Each depth level offsets the row by 14px
// and draws a thin vertical guide on the left edge so the parent/child
// relationship reads at a glance without per-row icons.
@for $d from 1 through 4 {
  .items-list :deep(.q-item.depth-#{$d}) {
    padding-left: #{($d * 14) + 6}px;

    &::before {
      content: '';
      position: absolute;
      left: #{($d * 14) - 8}px;
      top: 0;
      bottom: 0;
      width: 1px;
      background: rgba(255, 255, 255, 0.12);
    }
  }
}

.items-list :deep(.q-item.pinned-share-root) {
  font-weight: 500;

  // Group separator: each pinned chain (root + its linked descendants) reads
  // as a distinct unit. Skip on the first root since the items-list already
  // has its own top padding.
  &:not(:first-child) {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
  }

  .preview-cell {
    color: #cfe2ff;
  }
}

.pinned-share {
  position: relative;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid rgba(255, 255, 255, 0.04);
  transition: background 0.15s, border-color 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.08);
  }
}

.note {
  color: #e7eaee;
  line-height: 1.35;
  word-break: break-word;
}

.note-fallback {
  // CrudPreview renders an inline button; strip its chrome so it reads as a title
  :deep(.q-btn) {
    padding: 0;
    min-height: 0;
    background: transparent !important;
    text-align: left;
    justify-content: flex-start;
    font-weight: 500;
  }

  :deep(.q-btn__content) {
    padding: 0;
    justify-content: flex-start;
  }
}

.meta-line {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0 6px;
  margin-top: 2px;
  font-size: 0.7rem;
  line-height: 1.2;
  color: #8a93a2;
}

.meta-sender {
  display: inline-flex;
  align-items: center;
  color: #b8c0cc;
}

.meta-sep {
  opacity: 0.5;
}

.card-action {
  flex: 0 0 auto;
  margin-left: 2px;
}

.pinned-share-slide {
  // QSlideItem hardcodes `background: white` on the root and __content
  // inherits it. Setting transparent here exposes the left/right action slot
  // (e.g. red "Terminé" backdrop) when the user starts to swipe and cancels
  // — the content snaps back to translate(0,0) but its now-see-through
  // surface keeps the underlying action visible. Use the panel's own dark
  // surface color so the content stays opaque and covers the action when
  // the slide is centered.
  background: #14181e;
  border-radius: 10px;
  margin-bottom: 10px;
  overflow: hidden;

  &:last-child {
    margin-bottom: 0;
  }
}

.slide-action {
  display: flex;
  align-items: center;
  width: 100%;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.slide-action-left {
  justify-content: flex-start;
  padding-left: 16px;
}

.items-list {
  background: rgba(0, 0, 0, 0.22);
  border-radius: 6px;
  margin-top: 8px;
  padding: 2px;
  overflow: visible;
}

.pinned-share-active {
  background: linear-gradient(90deg, rgba(96, 165, 250, 0.45), rgba(96, 165, 250, 0.22)) !important;
  border-left: 3px solid #60a5fa;
  box-shadow: inset 0 0 0 1px rgba(96, 165, 250, 0.35);
  cursor: default !important;

  &:hover {
    background: linear-gradient(90deg, rgba(96, 165, 250, 0.45), rgba(96, 165, 250, 0.22)) !important;
  }

  :deep(.preview-cell) {
    color: #ffffff !important;
    text-decoration: none !important;
    font-weight: 700;
  }

  :deep(.q-focus-helper) {
    display: none;
  }
}

.share-check {
  flex: 0 0 auto;
}

.info-cell {
  min-width: 24px;
  padding-left: 4px;
  padding-right: 0;
}

@media (max-width: 599px) {
  .pinned-shares-panel :deep(.q-btn--dense) {
    padding: 4px 6px;
    min-height: 28px;
  }
}

.preview-cell {
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;

  :deep(.q-btn) {
    max-width: 100%;
    min-width: 0;
    text-align: left;
    justify-content: flex-start;
  }

  :deep(.q-btn__content) {
    max-width: 100%;
    min-width: 0;
    flex-wrap: nowrap;
    overflow: hidden;
  }

  :deep(.q-btn__content > div) {
    max-width: 100%;
    min-width: 0;
    overflow: hidden;
    flex-wrap: nowrap;
  }

  // The leaf div that actually holds the title text
  :deep(.q-btn__content > div > div) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
    max-width: 100%;
    display: block;
  }
}
</style>
